import { StateCreator } from "zustand";

export type EventIndicatorType =
  | { type: EventIndicatorStateType; message: string; index: number }
  | undefined;
export type ConfirmationModalData =
  | {
      title: string;
      text: string;
      confirmText?: string;
      cancelText?: string;
      resolve: ((value: boolean) => void) | null;
      /**
       * Called when the dialog closes without an answer (a tap outside, a logout, another dialog) instead of the
       * cancel answer, where cancelling writes something.
       */
      onDismiss?: () => void;
    }
  | undefined;
export type EventIndicatorStateType = "error" | "success" | "info" | "warning";

export type AppState = {
  testNumber: number | undefined;
  eventIndicatorData?: EventIndicatorType;
  confirmationModalData?: ConfirmationModalData;
  loggedIn?: boolean;
  userId?: string;
  loggingOut?: boolean;
  authReady: boolean;
  language?:
    | "de"
    | "en"
    | "tr"
    | "cs"
    | "ru"
    | "hr"
    | "es"
    | "fr"
    | "it"
    | "sq";

  setLanguage: (
    language:
      | "de"
      | "en"
      | "tr"
      | "cs"
      | "ru"
      | "hr"
      | "es"
      | "fr"
      | "it"
      | "sq",
  ) => void;
  setLoggedIn: (loggedIn: boolean) => void;
  setUserId: (userId?: string) => void;
  setLoggingOut: (loggingOut: boolean) => void;
  setAuthReady: (authReady: boolean) => void;
  setConfirmationModalData: (
    confirmationModalData?: ConfirmationModalData,
  ) => void;
  dismissConfirmationModal: () => void;
  setEventIndicatorData: (
    type: EventIndicatorStateType,
    message: string,
  ) => void;
  setTestNumber: (testNumber: number) => void;
  resetAppState: () => void;
};

const initialState = {
  testNumber: undefined,
  confirmationModalData: undefined,
  eventIndicatorData: undefined,
  loggedIn: undefined,
  userId: undefined,
  language: undefined,
};

export const createAppSlice: StateCreator<AppState> = (set, get) => ({
  ...initialState,
  loggingOut: false,
  authReady: false,
  setLanguage: (
    language:
      | "de"
      | "en"
      | "tr"
      | "cs"
      | "ru"
      | "hr"
      | "es"
      | "fr"
      | "it"
      | "sq",
  ) => set(() => ({ language })),
  setEventIndicatorData: (type: EventIndicatorStateType, message: string) =>
    set((state: AppState) => {
      state.eventIndicatorData = {
        type,
        message,
        index: state.eventIndicatorData?.index
          ? state.eventIndicatorData?.index + 1
          : 1,
      };
      return state;
    }),
  // Every caller of a dialog waits for it to settle, so a dialog replaced by another one settles as dismissed.
  setConfirmationModalData: (confirmationModalData?: ConfirmationModalData) => {
    const open = get().confirmationModalData;
    if (confirmationModalData && open && open !== confirmationModalData) get().dismissConfirmationModal();
    set({ confirmationModalData });
  },
  dismissConfirmationModal: () => {
    const open = get().confirmationModalData;
    if (!open) return;
    set({ confirmationModalData: undefined });
    if (open.onDismiss) open.onDismiss();
    else open.resolve?.(false);
  },

  setUserId: (userId?: string) => set(() => ({ userId })),
  setLoggedIn: (loggedIn: boolean) => set(() => ({ loggedIn })),
  setLoggingOut: (loggingOut: boolean) => set(() => ({ loggingOut })),
  setAuthReady: (authReady: boolean) => set(() => ({ authReady })),
  setTestNumber: (testNumber: number) => set(() => ({ testNumber })),
  resetAppState: () => set(initialState),
});

export const getTestNumber = (state: AppState) => state.testNumber;
export const getEventIndicatorData = (state: AppState) =>
  state.eventIndicatorData;
export const getConfirmationModalData = (state: AppState) =>
  state.confirmationModalData;
export const getLoggedIn = (state: AppState) => state.loggedIn;
export const getLoggingOut = (state: AppState) => state.loggingOut;
export const getAuthReady = (state: AppState) => state.authReady;
export const getUserId = (state: AppState) => state.userId;
export const getLanguage = (state: AppState) =>
  state.language ?? window.navigator.language.split("-")[0];
